import React from 'react';
import PageHeader from '../components/PageHeader';
import Callout from '../components/Callout';
import { motion } from 'framer-motion';

const Reporting = () => {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="prose-custom">
      <PageHeader
        badge="Stage 4"
        title="Reporting & Transparency"
        subtitle="Reproducibility is only possible if the record is honest and complete. What you disclose—and how—determines whether anyone can check your work."
      />

      <p>
        A perfectly designed study analyzed with a perfectly reproducible pipeline can still be
        irreproducible in practice if the published account omits what a reader would need to
        evaluate or repeat it. Transparency is not an add-on at submission time; it is the bridge
        between the work you did and the work others can trust and build on.
      </p>

      <h2>The TOP Guidelines</h2>
      <p>
        The <strong>Transparency and Openness Promotion (TOP) Guidelines</strong>
        (<a href="https://www.science.org/doi/10.1126/science.aab2374" target="_blank" rel="noopener noreferrer">Nosek et al., 2015</a>)
        are a shared framework that journals and funders use to set expectations across eight
        standards—including data transparency, analytic code transparency, research materials
        transparency, and preregistration. Each standard can be adopted at increasing levels of
        stringency (disclose, require, verify). Thousands of journals have signed on, so checking
        a target journal's TOP level tells you what you will be expected to share before you submit.
      </p>
      <Callout type="info" title="Find your journal's expectations early">
        Review the transparency and data-availability policy of your target journal
        <em> before</em> you finish the study, not after. The
        <a href="https://www.cos.io/top" target="_blank" rel="noopener noreferrer"> TOP Guidelines</a> at
        the Center for Open Science summarize what "disclose," "require," and "verify" mean in practice.
      </Callout>

      <h2>Transparent Methods Reporting</h2>
      <p>
        The methods section is where reproducibility is won or lost. Report enough that an
        independent expert could repeat the procedure: exact reagents and catalog numbers, software
        and package versions, parameter settings, inclusion/exclusion rules, and every analytic
        decision—including the ones that did not "work." Where a community reporting standard exists
        for your study type (ARRIVE, CONSORT, STROBE, PRISMA and others, catalogued by the
        <a href="https://www.equator-network.org/" target="_blank" rel="noopener noreferrer"> EQUATOR Network</a>),
        follow it: these checklists exist precisely to capture the details authors habitually omit.
      </p>

      <h2>Data and Code Availability Statements</h2>
      <p>
        "Available upon request" is widely recognized as a weak commitment—requests frequently go
        unanswered, and availability decays as authors move institutions. A strong availability
        statement points to a specific, persistent location (a repository with a DOI) for the data
        and the analysis code, or gives a concrete, justified reason and a governed access route when
        data are sensitive. In the United States, the 2023 NIH Data Management and Sharing Policy now
        requires a data management and sharing plan for applicable funding, which makes planning for
        this disclosure a compliance step, not just good practice.
      </p>

      <h2>Reporting Negative and Null Results</h2>
      <p>
        Publication bias—the tendency for only positive findings to appear in print—is one of the
        structural drivers of irreproducibility discussed in <em>The Evidence Base</em>. Reporting
        well-designed studies that returned null or negative results is part of the fix, not a mark
        against the work.
        <strong> Registered Reports</strong> (introduced under Design &amp; Planning) address this at
        the source by securing peer review and in-principle acceptance <em>before</em> results are
        known, so the finding's direction cannot decide whether it is published.
      </p>

      <Callout type="success" title="Open Science Badges">
        Some journals award badges for Open Data, Open Materials, and Preregistration—a lightweight,
        visible signal that the underlying research objects are actually available. They are a low-cost
        way to make transparency legible to readers and reviewers.
      </Callout>
    </motion.div>
  );
};

export default Reporting;
